import { ratios } from '@/styles/layout';
import { viewportSizes } from '@/styles/media';
import { adjustImageAsset } from '@/utils/imageUtils';

export class ResponsiveImageHints {
    public displayRatio;

    constructor(
        private sizes: ISizes,
        public readonly ratio: number | null = null,
        displayRatio: number | null = null
    ) {
        this.displayRatio = displayRatio ?? ratio ?? ratios.media;
    }

    public getSrcSet = (src: string) => {
        // provide a series of srcs for the browser to choose from.
        // It will decide based on viewport width (and sizes declaration), as well as device pixel ratio
        return [
            `${this.calculateSrcSet(src, this.ratio, 80)}`,
            `${this.calculateSrcSet(src, this.ratio, 160)}`,
            `${this.calculateSrcSet(src, this.ratio, 320)}`,
            `${this.calculateSrcSet(src, this.ratio, 480)}`,
            `${this.calculateSrcSet(src, this.ratio, 640)}`,
            `${this.calculateSrcSet(src, this.ratio, 768)}`,
            `${this.calculateSrcSet(src, this.ratio, 896)}`,
            `${this.calculateSrcSet(src, this.ratio, 1008)}`,
            `${this.calculateSrcSet(src, this.ratio, 1280)}`,
            `${this.calculateSrcSet(src, this.ratio, 1920)}`,
            `${this.calculateSrcSet(src, this.ratio, 2560)}`,
        ].join(', ');
    };

    public getSizes = () => {
        return [
            this.sizes.atMAXIMUM && this.sizeAtBreakpoint(viewportSizes.MAXIMUM, this.sizes.atMAXIMUM),
            this.sizes.atXXL && this.sizeAtBreakpoint(viewportSizes.XXL, this.sizes.atXXL),
            this.sizes.atXL && this.sizeAtBreakpoint(viewportSizes.XL, this.sizes.atXL),
            this.sizes.atL && this.sizeAtBreakpoint(viewportSizes.L, this.sizes.atL),
            this.sizes.atM && this.sizeAtBreakpoint(viewportSizes.M, this.sizes.atM),
            this.sizes.atS && this.sizeAtBreakpoint(viewportSizes.S, this.sizes.atS),
            this.sizes.atXS && this.sizeAtBreakpoint(viewportSizes.XS, this.sizes.atXS),
            `${this.sizes.initial}`,
        ]
            .filter(Boolean)
            .join(',');
    };

    private calculateSrcSet = (src: string, ratio: number | null, width: number) => {
        const height = ratio ? Math.round(width / ratio) : -1;
        return `${adjustImageAsset(src, width, height)} ${width}w`;
    };

    private sizeAtBreakpoint = (breakpointInPx: number, imageWidth: CSSStyleDeclaration['width']) =>
        `(min-width: ${breakpointInPx}px) ${imageWidth}`;
}

interface ISizes {
    /** Size for initial state (e.g 25vw or 40px) */
    initial: CSSStyleDeclaration['width'];
    /** Size for XS breakpoint (e.g 25vw or 40px) */
    atXS?: CSSStyleDeclaration['width'];
    /** Size for S breakpoint (e.g 25vw or 40px) */
    atS?: CSSStyleDeclaration['width'];
    /** Size for M breakpoint (e.g 25vw or 40px) */
    atM?: CSSStyleDeclaration['width'];
    /** Size for L breakpoint (e.g 25vw or 40px) */
    atL?: CSSStyleDeclaration['width'];
    /** Size for XL breakpoint (e.g 25vw or 40px) */
    atXL?: CSSStyleDeclaration['width'];
    /** Size for XXL breakpoint (e.g 25vw or 40px) */
    atXXL?: CSSStyleDeclaration['width'];
    /** Size for MAXIMUM breakpoint (e.g 25vw or 40px) */
    atMAXIMUM?: CSSStyleDeclaration['width'];
}
